import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn, LUXON_DATE_FORMAT } from "@gymflow/helpers";
import { CustomPaymentCategoryDTO, UserMemberBean } from "@gymflow/types";
import { useQuery } from "@tanstack/react-query";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { Formik, useFormikContext } from "formik";
import { apiV2, queryClient } from "libs/api/src/lib/apiV2";
import { DateTime } from "luxon";
import React, {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { usePortalRoutes } from "../../../hooks";
import { useClubSettings } from "../../../providers";
import { UserMemberPage } from "../../../views/UserMember";
import {
  Button,
  SlideSideBar,
  Spinner,
  TabType,
  UsersSearchInput,
  UsersSearchInputOptionsType,
  UsersSearchInputOptionType,
} from "../../atoms";
import { OptionType } from "../../atoms/base/components/OptionsList/components/OptionItem";
import CashIcon from "../../atoms/icons/CashIcon";
import { CreditCard02Icon } from "../../atoms/icons/CreditCard02Icon";
import { CollapsibleSections } from "../../molecules";
import { SideBarUserForm } from "../../molecules/SideBarUserForm";
import { ShopCheckout } from "../ShopCheckout";
import { ShopItemPicker } from "../ShopItemPicker";
import { ShopCartType } from "../ShopTypes";
import { useProcessCheckout } from "./hooks/useProcessCheckout";
import { ShopSidebarContext } from "./ShopSidebarContext";

export type TabsType = "ONLINE" | "IN_PERSON";

export type ShopSidebarFormValues = {
  priceOverride?: number;
  inPersonPaymentMethod?: OptionType<CustomPaymentCategoryDTO>;
  paymentMethodId?: string;
  walletAmount?: number;
};

export const TABS: TabType<TabsType>[] = [
  {
    id: "ONLINE",
    label: (isActive) => (
      <>
        <CreditCard02Icon
          className="size-7"
          pathClassName={cn("stroke-1", { "stroke-secondary-700": isActive })}
        />
        Online
      </>
    ),
  },
  {
    id: "IN_PERSON",
    label: (isActive) => (
      <>
        <CashIcon
          className="size-7"
          pathClassName={cn({ "fill-secondary-700": isActive })}
        />
        In Person
      </>
    ),
  },
];

// need to refactor it later to separate components + add form logic (for whole component, not useState)
export const ShopSidebarSidebarProvider: React.FC<{
  children: ReactElement | ReactElement[];
}> = ({ children }) => {
  const { t } = useTranslation();
  const [sidebarState, setSidebarState] = useState<
    | {
        isVisible: boolean;
        currentShopTab?: number;
      }
    | undefined
  >();
  const [isUserFormShown, setIsUserFormShown] = useState(false);
  const [activeTabId, setActiveTabId] = useState<TabsType>(TABS[0].id);
  const [orderKey, setOrderKey] = useState<string>();
  const { club } = useClubSettings();
  const hide = useCallback(() => {
    setSidebarState(undefined);
    setIsUserFormShown(false);
    setSelectedUser(undefined);
    _setCart(undefined);
    setPromotionCode(undefined);
    setStartDate(
      DateTime.now()
        .setZone(club.timezone)
        .startOf("day")
        .toFormat(LUXON_DATE_FORMAT),
    );
    setAllowChangingStartDate(true);
    setOpenSectionIdx(0);
    setOrderKey(undefined);
    setActiveTabId(TABS[0].id);
  }, [club.timezone]);
  const [selectedUser, setSelectedUser] =
    useState<UsersSearchInputOptionType>();
  const selectedUserId = selectedUser?.value.id;
  const selectedUserProfileType = selectedUser?.value.profileType;
  const [cart, _setCart] = useState<ShopCartType>();

  const setCart = useCallback(
    (newCart: ShopCartType, stayInSection?: boolean) => {
      _setCart(newCart);
      if (!stayInSection) {
        setOpenSectionIdx(2);
      }
      if (newCart.type === "MEMBERSHIP" && newCart.payload.fixedStartDate) {
        setStartDate(
          DateTime.fromISO(newCart.payload.fixedStartDate, {
            setZone: true,
          })
            .plus({ seconds: 1 })
            .setZone(club.timezone)
            .toFormat(LUXON_DATE_FORMAT),
        );
        setAllowChangingStartDate(false);
      } else {
        setStartDate(
          DateTime.now()
            .setZone(club.timezone)
            .startOf("day")
            .toFormat(LUXON_DATE_FORMAT),
        );
        setAllowChangingStartDate(true);
      }
    },
    [club.timezone],
  );

  const cartHasSomething =
    cart?.type === "MEMBERSHIP" ||
    cart?.type === "CREDIT_PACK" ||
    (cart?.type === "GIFT_VOUCHER" &&
      !!cart.payload?.amount &&
      !!Number.isInteger(cart.payload.amount)) ||
    (cart?.type === "PRODUCT" && cart?.payload?.some((e) => e.quantity > 0));

  const [promotionCode, setPromotionCode] = useState<string>();
  const [startDate, setStartDate] = useState<string>(
    DateTime.now()
      .setZone(club.timezone)
      .startOf("day")
      .toFormat(LUXON_DATE_FORMAT),
  );
  const [allowChangingStartDate, setAllowChangingStartDate] =
    useState<boolean>(true);

  const { toast } = useContext(ToastContext);
  const { processCheckout } = useProcessCheckout();

  const customPaymentsEnabled = club?.customPaymentsEnabled ?? false;

  const { data: currentWalletBalance } = useQuery(
    apiV2.staff.wallet.queries.list(
      typeof selectedUser?.id === "string" ? selectedUser.id : undefined,
    ),
  );

  const { data: user, isFetching: isFetchingUserMember } = useQuery(
    apiV2.staff.member.queries.idDeprecated(
      selectedUserProfileType === "USER"
        ? (selectedUserId as string)
        : undefined,
    ),
  );

  const userHasActiveMembershipSubscription = user?.subscriptions.some(
    (e) =>
      !!e.membershipBean &&
      (e.active ||
        e.status === "OVERDUE" ||
        e.status === "PENDING" ||
        e.status === "PAUSED"),
  );

  // just "copy-paste"; will change it with new design
  const onMemberCreated = useCallback(async (newMemberId: string) => {
    if (!newMemberId) return;

    const newMember = await queryClient.fetchQuery(
      apiV2.staff.member.queries.idDeprecated(newMemberId),
    );
    setSelectedUser({
      id: newMember.id,
      label: `${newMember.firstName} ${newMember.lastName}`,
      subLabel: newMember.email,
      value: newMember,
      imageUrl: newMember.picture,
    });

    setOpenSectionIdx(1);
    _setCart(undefined);
    setIsUserFormShown(false);
  }, []);

  const onChangeUsersSearchInput = useCallback(
    async (selected: UsersSearchInputOptionsType) => {
      const selectedLocal = selected[0];
      setSelectedUser(selectedLocal);
      _setCart(undefined);
      setOpenSectionIdx(0);
      setIsUserFormShown(false);

      if (selectedLocal?.value.profileType === "LEAD") {
        return;
      } else if (
        typeof selectedLocal?.value.id === "string" &&
        selectedLocal?.value.profileType === "USER"
      ) {
        // if user id is string type -> it's UserMemberBean (we check it above)
        setOpenSectionIdx(1);
      }
    },
    [],
  );

  const buttonLabel = useMemo(() => {
    if (selectedUserProfileType === "LEAD") {
      return t("components.shopSidebarProvider.button.setupAccount");
    } else {
      return t("components.shopSidebarProvider.button.newUser");
    }
  }, [selectedUserProfileType, t]);

  const infoLabel = useMemo(() => {
    if (selectedUserProfileType === "LEAD") {
      return t("components.shopSidebarProvider.info.lead");
    } else {
      return "";
    }
  }, [selectedUserProfileType, t]);

  const sections = [
    {
      title: "Select User",
      body: (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-2">
            <div className="flex-1">
              <UsersSearchInput
                withImageByDefault
                value={selectedUser ? [selectedUser] : []}
                onChange={onChangeUsersSearchInput}
                isMulti={false}
              />
            </div>
            <Button
              className="min-w-fit"
              onClick={() => setIsUserFormShown(true)}
            >
              {buttonLabel}
            </Button>
          </div>
          <div className="text-sm text-gray-600">{infoLabel}</div>
          {isUserFormShown && (
            <SideBarUserForm
              className="px-0"
              onMemberCreated={onMemberCreated}
              defaultValues={{
                email: selectedUser?.value?.email ?? "",
                firstName: selectedUser?.value?.firstName ?? "",
                lastName: selectedUser?.value?.lastName ?? "",
                mobileNumber: selectedUser?.value?.mobileNumber ?? "",
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: "Select Item",
      body: (
        <div className="flex w-full flex-row items-center gap-2">
          {isFetchingUserMember && (
            <div className="w-full items-center p-4">
              <Spinner />
            </div>
          )}
          {!isFetchingUserMember && (
            <ShopItemPicker
              userHasActiveMembershipSubscription={
                !!userHasActiveMembershipSubscription
              }
              cart={cart}
              setCart={setCart}
              hideShopSidebar={() => {
                setSidebarState((e) => ({
                  ...e,
                  isVisible: false,
                }));
              }}
              showShopSidebar={() => {
                setSidebarState((e) => ({
                  ...e,
                  isVisible: true,
                }));
              }}
              sidebarState={sidebarState}
              setSidebarState={setSidebarState}
            />
          )}{" "}
        </div>
      ),
      className: "p-0",
      disabled: !selectedUser || selectedUserProfileType === "LEAD",
    },
    {
      title: "Checkout",
      body: !!cart &&
        selectedUser &&
        typeof selectedUserId === "string" &&
        selectedUserProfileType === "USER" && (
          <ShopCheckout
            cart={cart}
            // if user id is string type -> it's UserMemberBean (we check it above)
            selectedUser={selectedUser.value as UserMemberBean}
            promotionCode={promotionCode}
            setPromotionCode={setPromotionCode}
            startDate={startDate}
            setStartDate={setStartDate}
            allowChangingStartDate={allowChangingStartDate}
            activeTabId={activeTabId}
            setActiveTabId={setActiveTabId}
            customPaymentsEnabled={customPaymentsEnabled}
            userHasActiveMembershipSubscription={
              !!userHasActiveMembershipSubscription
            }
            currentWalletBalance={currentWalletBalance?.walletBalance || 0}
          />
        ),
      disabled: !cart || !cartHasSomething,
    },
  ];
  const [openSectionIdx, setOpenSectionIdx] = useState<number>(0);

  const history = useHistory();
  const { createMemberLink } = usePortalRoutes();
  const showScaModal = useScaModal({
    asMember: false,
  });

  return (
    <ShopSidebarContext.Provider
      value={{
        showShop: async (user) => {
          if (user) {
            setSelectedUser({
              id: user.id,
              label: `${user.firstName} ${user.lastName}`,
              subLabel: user.email,
              value: user,
            });
            if (user.profileType === "USER" && typeof user.id === "string") {
              // if user id is string type -> it's UserMemberBean (we check it above)
              setOpenSectionIdx(1);
            }
          }
          setSidebarState({
            isVisible: true,
          });
          const newOrderKey = uuidv4();
          setOrderKey(newOrderKey);
        },
      }}
    >
      {children}
      <SlideSideBar
        isOpen={!!sidebarState?.isVisible}
        hide={hide}
        className="!w-full lg:!w-1/2"
      >
        {/* refactor this form (after redesign the sidebar) */}
        <Formik<ShopSidebarFormValues>
          initialValues={{
            priceOverride: undefined,
            inPersonPaymentMethod: undefined,
            paymentMethodId: undefined,
            walletAmount: undefined,
          }}
          validationSchema={toFormikValidationSchema(
            z
              .object({
                priceOverride: z.coerce
                  .number({
                    invalid_type_error:
                      cart?.type === "GIFT_VOUCHER"
                        ? t("validation.mustIncludeVoucherAmount")
                        : t("validation.mustIncludePrice"),
                    required_error:
                      cart?.type === "GIFT_VOUCHER"
                        ? t("validation.mustIncludeVoucherAmount")
                        : t("validation.mustIncludePrice"),
                  })
                  .min(0, "Minimum amount due is 0")
                  .refine(
                    (val) =>
                      cart?.type !== "GIFT_VOUCHER" || Number.isInteger(val),
                    { message: t("validation.mustBeWholeNumber") },
                  )
                  .refine((val) => cart?.type !== "GIFT_VOUCHER" || val >= 1, {
                    message: t("validation.minAmountOne"),
                  }),
                inPersonPaymentMethod: z.custom((e) => e).optional(),
                paymentMethodId: z.string().optional(),
                walletAmount: z
                  .number()
                  .min(0.5, "Min to apply - 0.50")
                  .max(
                    currentWalletBalance?.walletBalance || 0,
                    "Insufficient amount on balance",
                  )
                  .optional(),
              })
              .superRefine(
                (
                  { paymentMethodId, priceOverride, inPersonPaymentMethod },
                  ctx,
                ) => {
                  if (activeTabId === "IN_PERSON" && !inPersonPaymentMethod) {
                    ctx.addIssue({
                      path: ["inPersonPaymentMethod"],
                      code: z.ZodIssueCode.custom,
                      message: "This field is required",
                    });
                  }

                  if (
                    activeTabId === "ONLINE" &&
                    !paymentMethodId &&
                    priceOverride !== 0
                  ) {
                    ctx.addIssue({
                      path: ["paymentMethodId"],
                      code: z.ZodIssueCode.custom,
                      message: "Add payment method",
                    });
                  }

                  if (priceOverride !== undefined && priceOverride < 0) {
                    ctx.addIssue({
                      path: ["walletAmount"],
                      code: z.ZodIssueCode.custom,
                      message: "Wallet amount cannot exceed the total price",
                    });
                  }
                },
              ),
          )}
          onSubmit={async (values) => {
            if (!cart || !orderKey) {
              return;
            }

            // Checkout only for members (id is string)
            if (typeof selectedUserId === "number" || !selectedUserId) {
              return;
            }

            // empty string or undefined or 0 -> undefined
            const walletAmount = !values.walletAmount
              ? undefined
              : values.walletAmount;

            const priceOverride =
              values.priceOverride !== undefined
                ? +values.priceOverride + (walletAmount || 0) // Calculate price override including wallet amount (because BE needs total override price, yes...)
                : undefined;

            const result = await processCheckout({
              cart,
              activeTabId,
              orderKey,
              values,
              startDate,
              userMemberId: selectedUserId,
              promotionCode,
              paymentMethodId: values.paymentMethodId,
              priceOverride,
              customPaymentsEnabled,
              walletAmount,
            });

            if (
              cart.type === "GIFT_VOUCHER" &&
              result &&
              result?.invoiceStatus === "PAID"
            ) {
              toast({
                message: t("page.giftVouchers.voucherPurchased.title"),
                description: t(
                  "page.giftVouchers.voucherPurchased.description",
                  { customerEmail: selectedUser?.subLabel },
                ),
                intent: "success",
              });
            }

            hide();

            history.push(
              createMemberLink(
                selectedUserId,
                cart.type === "MEMBERSHIP"
                  ? UserMemberPage.Account
                  : cart.type === "CREDIT_PACK"
                    ? UserMemberPage.Sessions
                    : UserMemberPage.Payments,
              ),
            );

            showScaModal({
              invoiceNumber: result?.invoiceNumber || "",
              userMemberId: selectedUserId,
            });
          }}
        >
          <div className="flex h-full max-h-full flex-col overflow-hidden">
            <div className="flex flex-col justify-between border-b border-gray-200 p-8">
              <div className="mb-1 flex flex-row items-center justify-between">
                <div className="text-xl font-semibold text-gray-900">
                  Sell Something
                </div>

                <FontAwesomeIcon
                  onClick={() => {
                    hide();
                  }}
                  className="cursor-pointer text-xl text-gray-600"
                  icon={faClose}
                />
              </div>
              <div className="text-sm font-medium text-gray-600">
                Sell a membership, credit pack or products.
              </div>
              {selectedUser && (
                <div className="text-sm font-medium text-gray-600">
                  Sale For:{" "}
                  <b>
                    {selectedUser.value.firstName} {selectedUser.value.lastName}
                  </b>
                  {cartHasSomething && cart
                    ? `, ${
                        cart.type === "MEMBERSHIP" ||
                        cart.type === "GIFT_VOUCHER"
                          ? cart.payload.name
                          : cart.type === "CREDIT_PACK"
                            ? cart.payload.name
                            : cart.payload
                                .filter((e) => e.quantity > 0)
                                .map(
                                  (e) =>
                                    `${e.quantity > 1 ? `${e.quantity} x ` : ""}${
                                      e.product.name
                                    }`,
                                )
                                .join(", ")
                      }`
                    : ""}
                </div>
              )}
            </div>
            <CollapsibleSections
              sections={sections}
              onChangeSection={(_, sectionIdx) => {
                setOpenSectionIdx(sectionIdx);
              }}
              openSectionIndex={openSectionIdx}
            />
            <div className="mt-auto flex h-20 w-full flex-row items-center justify-end gap-x-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
              <Button
                className="flex-1"
                onClick={() => {
                  hide();
                }}
              >
                Cancel
              </Button>
              {cart && openSectionIdx === 2 && <TakePaymentButton />}
            </div>
          </div>
        </Formik>
      </SlideSideBar>
    </ShopSidebarContext.Provider>
  );
};
const TakePaymentButton: React.FC = () => {
  const formik = useFormikContext();
  return (
    <Button
      className="flex-1"
      intent="primary"
      disabled={!formik.isValid}
      onClick={async () => {
        await formik.submitForm();
      }}
    >
      Take Payment
    </Button>
  );
};
